// src/pages/PrivateRideBooking.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../services/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { usePrivateRideInsightTracking } from "../pages/admin/hooks/usePrivateRideInsightTracking";
import {
  inferBookingFunnelReasonCode,
  trackBookingFunnelEvent,
} from "../utils/bookingFunnelTracking";

// Keep the picker as a normal route-level dependency. PrivateRideBooking itself
// is already code-split from App.js, so MapLibre is still excluded from the
// global initial bundle while avoiding a second async chunk boundary when the
// full-screen WebGL map is opened.
import UniAirLocationPicker from '../components/UniAirLocationPicker';
import PrivateRideRoutePreview from './private-rides/PrivateRideRoutePreview';
import PrivateRideInlineLocationField from './private-rides/PrivateRideInlineLocationField';
import RapidRideNowPayment from '../components/RapidRideNowPayment';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';

// Shared passenger-app typography: Manrope provides the premium, professional
// visual rhythm used on the Airport Booking flow. The fallback stack remains
// polished when the remote font cannot be loaded.
const UNIAIR_PREMIUM_FONT_STYLESHEET_ID = 'uniair-premium-manrope-font';
const UNIAIR_PREMIUM_FONT_STYLESHEET_URL =
  'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=optional';
const UNIAIR_PREMIUM_FONT_FAMILY =
  "'Manrope', 'Avenir Next', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const UNIAIR_PREMIUM_TYPOGRAPHY = {
  fontFamily: UNIAIR_PREMIUM_FONT_FAMILY,
  fontKerning: 'normal',
};


function PrivateRideLocationIcon({ className = 'h-4 w-4' }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
    </svg>
  );
}


function UniAirMapPickerPin({ className = 'h-14 w-10' }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 48 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24 3.5C12.8 3.5 3.8 12.5 3.8 23.6c0 14.9 16.8 31.2 20.2 36.8 3.4-5.6 20.2-21.9 20.2-36.8C44.2 12.5 35.2 3.5 24 3.5Z"
        fill="#720e1e"
        stroke="white"
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="23.8" r="8" fill="#d6ad60" stroke="white" strokeWidth="3" />
    </svg>
  );
}

const formatMoney = (value) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const pad2 = (value) => String(value).padStart(2, "0");

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  return `${year}-${month}-${day}`;
};

const toTimeInput = (date) => {
  const hours = pad2(date.getHours());
  const minutes = pad2(date.getMinutes());
  return `${hours}:${minutes}`;
};

const addMinutes = (date, minutes) =>
  new Date(date.getTime() + minutes * 60 * 1000);

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + Number(days || 0));
  return copy;
};

const roundUpToQuarterHour = (date = new Date()) => {
  const copy = new Date(date);
  copy.setSeconds(0, 0);

  const remainder = copy.getMinutes() % 15;
  if (remainder !== 0) {
    copy.setMinutes(copy.getMinutes() + (15 - remainder));
  }

  return copy;
};

const buildLocalDateTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;

  const parsed = new Date(`${dateValue}T${timeValue}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  const parsed = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateValue;

  return parsed.toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (timeValue) => String(timeValue || "").slice(0, 5);

const formatNoticeWindow = (value) => {
  const minutes = Math.max(0, Number(value || 0));

  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }

  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hour${hours === 1 ? "" : "s"} ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`;
};

const isBlank = (value) => !String(value || "").trim();

const STANDARD_PRIVATE_RIDE_MAX_PASSENGERS = 4;
const GROUP_RIDE_MIN_PASSENGERS = 5;
const INSTANT_TWO_CAR_GROUP_MAX_PASSENGERS = 8;

const createPrivateRideJourneySessionId = () => {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    },
  );
};

const buildImmediateRecoverySessionKey = (prefix, userId) => {
  const randomPart =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  return `${prefix}:${userId || "anonymous"}:${randomPart}`.slice(0, 220);
};

const compactRecoveryLocation = (point, fallbackLabel = "") => {
  const label = String(point?.label || fallbackLabel || "").trim();
  const formattedAddress = String(
    point?.formattedAddress || point?.formatted_address || label,
  ).trim();

  if (!label && !formattedAddress) return null;

  const latitude = Number(point?.latitude);
  const longitude = Number(point?.longitude);

  return {
    label: label || formattedAddress,
    formatted_address: formattedAddress || label,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
  };
};

const recordImmediateNoAvailabilityRecovery = async ({
  marketId,
  productCode,
  userId,
  sessionKey,
  reasonCode,
  pageSource,
  journeyContext,
}) => {
  try {
    const { data, error } = await supabase.rpc(
      "record_immediate_no_availability_recovery_v1",
      {
        p_market_id: marketId || null,
        p_product_code: productCode,
        p_session_key:
          sessionKey || buildImmediateRecoverySessionKey(productCode, userId),
        p_reason_code: reasonCode,
        p_page_source: pageSource,
        p_journey_context: journeyContext || {},
      },
    );

    if (error) throw error;
    return data || null;
  } catch (error) {
    // Recovery analytics must not interrupt quote or booking UX.
    console.warn(
      "[Journey Recovery] Could not capture Private Ride no-availability details:",
      error?.message || error,
    );
    return null;
  }
};

const resolveMyJourneyRecoveryAfterBooking = async ({
  productCode,
  entityType,
  entityId,
}) => {
  try {
    const { error } = await supabase.rpc(
      "resolve_my_journey_recovery_after_booking_v1",
      {
        p_product_code: productCode,
        p_entity_type: entityType || null,
        p_entity_id: entityId ? String(entityId) : null,
      },
    );

    if (error) throw error;
  } catch (error) {
    console.warn(
      "[Journey Recovery] Could not close the recovered Private Ride case:",
      error?.message || error,
    );
  }
};

export default function PrivateRideBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const scheduledReturnDraft = location.state?.privateRideMode === 'scheduled'
    ? location.state?.returnBookingDraft
    : null;
  const analyticsPageOpenedRef = useRef(false);
  const lastTrackedQuoteIdRef = useRef(null);
  const privateRideJourneySessionIdRef = useRef(null);

  if (!privateRideJourneySessionIdRef.current) {
    privateRideJourneySessionIdRef.current = createPrivateRideJourneySessionId();
  }

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const existing =
      document.getElementById(UNIAIR_PREMIUM_FONT_STYLESHEET_ID) ||
      document.querySelector(`link[href="${UNIAIR_PREMIUM_FONT_STYLESHEET_URL}"]`);

    if (existing) return undefined;

    const link = document.createElement('link');
    link.id = UNIAIR_PREMIUM_FONT_STYLESHEET_ID;
    link.rel = 'stylesheet';
    link.href = UNIAIR_PREMIUM_FONT_STYLESHEET_URL;
    document.head.appendChild(link);

    return undefined;
  }, []);


  const initialDateTime = useMemo(
    () => addMinutes(roundUpToQuarterHour(new Date()), 30),
    [],
  );

  const [bookingOptions, setBookingOptions] = useState(null);
  const [groupRideOptions, setGroupRideOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [pickupPoint, setPickupPoint] = useState(null);
  const [dropoffPoint, setDropoffPoint] = useState(null);
  const [additionalStops, setAdditionalStops] = useState([]);

  const [bookingDate, setBookingDate] = useState(toDateInput(initialDateTime));
  const [pickupTime, setPickupTime] = useState(toTimeInput(initialDateTime));
  const [isAsap, setIsAsap] = useState(false);

  const [passengerCount, setPassengerCount] = useState(1);

  const [isReturnTrip, setIsReturnTrip] = useState(false);
  // The scheduled return is a second one-way booking; the outward Ride Now
  // is never recreated or charged on this form.
  useEffect(() => {
    if (!scheduledReturnDraft) return;
    const pickup = scheduledReturnDraft.pickupPoint;
    const dropoff = scheduledReturnDraft.dropoffPoint;
    const valid = (point) => point &&
      typeof point.label === 'string' && point.label.trim().length > 0 &&
      Number.isFinite(Number(point.latitude)) &&
      Math.abs(Number(point.latitude)) <= 90 &&
      Number.isFinite(Number(point.longitude)) &&
      Math.abs(Number(point.longitude)) <= 180;
    if (!valid(pickup) || !valid(dropoff)) return;
    setPickupPoint(pickup);
    setPickupLocation(pickup.label);
    setDropoffPoint(dropoff);
    setDropoffLocation(dropoff.label);
    setPassengerCount(Math.max(1, Math.min(4, Math.floor(Number(scheduledReturnDraft.passengerCount) || 1))));
    setIsAsap(false);
    setIsReturnTrip(false);
  }, [scheduledReturnDraft]);
  const [returnMode, setReturnMode] = useState("separate_return");
  const [returnDate, setReturnDate] = useState(toDateInput(initialDateTime));
  const [returnTime, setReturnTime] = useState("22:00");
  const [waitingHours, setWaitingHours] = useState(1);

  const [notes, setNotes] = useState("");

  const [instantQuote, setInstantQuote] = useState(null);
  const [instantQuoteLoading, setInstantQuoteLoading] = useState(false);
  const [instantQuoteError, setInstantQuoteError] = useState("");
  const [instantQuoteNotice, setInstantQuoteNotice] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [rapidRequestId, setRapidRequestId] = useState(null);
  const [rapidAutoBegin, setRapidAutoBegin] = useState(false);
  const [rapidPilotEligible, setRapidPilotEligible] = useState(false);
  const [productStatus, setProductStatus] = useState(null);
  // Paystack returns to this same route after authorization. A request ID
  // never constitutes payment evidence; the authenticated RPC verifies it.
  useEffect(() => {
    if (!user?.id) return;
    const params = new URLSearchParams(location.search);
    const callbackRequest = params.get("rapid_request");
    const validCallback = params.get("rapid_return") === "1" &&
      callbackRequest && /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(callbackRequest);
    if (!validCallback && location.state?.privateRideMode === "scheduled") return;
    try {
      const stored = window.sessionStorage.getItem(`uniair-rapid-request:${user.id}`);
      const selected = validCallback ? callbackRequest : stored;
      if (selected && /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(selected)) {
        window.sessionStorage.setItem(`uniair-rapid-request:${user.id}`, selected);
        const originalSession = window.sessionStorage.getItem(`uniair:rapid:journey:${selected}`);
        if (originalSession && /^[0-9a-f-]{36}$/i.test(originalSession)) {
          privateRideJourneySessionIdRef.current = originalSession;
        }
        setRapidRequestId(selected);
      }
    } catch {
      if (validCallback) setRapidRequestId(callbackRequest);
    }
  }, [user?.id, location.state?.privateRideMode, location.search]);
  const [activeLocationPicker, setActiveLocationPicker] = useState(null);
  const [step, setStep] = useState(1);

  const settings = bookingOptions?.settings || {};
  const minimumNoticeMinutes = Math.max(
    0,
    Number(settings.minimum_notice_minutes ?? 30),
  );
  const minimumNoticeLabel = formatNoticeWindow(minimumNoticeMinutes);
  const lateNightPricingEnabled =
    Number(settings.late_night_surcharge || 0) > 0;
  const maxGroupPassengers = Math.max(
    GROUP_RIDE_MIN_PASSENGERS,
    Number(groupRideOptions?.max_group_passengers || 100),
  );
  const isGroupRide = Number(passengerCount) >= GROUP_RIDE_MIN_PASSENGERS;
  const isInstantTwoCarGroup =
    Number(passengerCount) >= GROUP_RIDE_MIN_PASSENGERS &&
    Number(passengerCount) <= INSTANT_TWO_CAR_GROUP_MAX_PASSENGERS;
  const groupQuoteRequired =
    Number(passengerCount) > INSTANT_TWO_CAR_GROUP_MAX_PASSENGERS;
  const groupRidesUnavailable = groupRideOptions?.enabled === false;

  const selectStandardPassengerCount = (count) => {
    setPassengerCount(
      Math.max(1, Math.min(STANDARD_PRIVATE_RIDE_MAX_PASSENGERS, Number(count || 1))),
    );
  };

  const selectGroupRide = () => {
    // Group Ride quotes stay in scheduled Private Rides, never Ride Now.
    if (isAsap || groupRidesUnavailable) return;
    setPassengerCount((current) =>
      Math.max(GROUP_RIDE_MIN_PASSENGERS, Number(current || GROUP_RIDE_MIN_PASSENGERS)),
    );
  };
  const analyticsMarketId =
    user?.market_id ||
    bookingOptions?.market_id ||
    bookingOptions?.market?.market_id ||
    bookingOptions?.service_market_id ||
    null;

  useEffect(() => {
    if (!user?.id || !analyticsMarketId) {
      setRapidPilotEligible(false);
      return undefined;
    }
    let active = true;
    void supabase.rpc('uniair_rapid_my_pilot_eligibility_v1', {
      p_market_id: analyticsMarketId,
    }).then(({ data, error }) => {
      if (active) setRapidPilotEligible(!error && data?.enabled === true);
    });
    return () => { active = false; };
  }, [user?.id, analyticsMarketId]);

  useEffect(() => {
    if (!user?.id || !analyticsMarketId) {
      setProductStatus(null);
      return undefined;
    }
    let active = true;
    const refresh = async () => {
      const { data, error } = await supabase.rpc('uniair_get_my_product_status_v1');
      if (active && !error && data?.ok) setProductStatus(data);
    };
    void refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { active = false; document.removeEventListener('visibilitychange', onVisible); };
  }, [user?.id, analyticsMarketId]);

  // Ride Now pays for the outward single-car journey only. The requested
  // return is a separately confirmed scheduled booking, not part of the hold.
  const rideNowReturnLater = rapidPilotEligible && isAsap && isReturnTrip &&
    returnMode === 'separate_return';
  const rapidPilotRideNow = rapidPilotEligible && productStatus?.ride_now !== false && isAsap && !isGroupRide &&
    (!isReturnTrip || rideNowReturnLater);

  useEffect(() => {
    // The dashboard's Ride Now shortcut only selects the existing ASAP
    // booking mode after server-confirmed pilot eligibility. The backend
    // performs the actual payment/driver matching authorization.
    if (location.state?.privateRideMode === 'ride_now' && rapidPilotEligible && productStatus?.ride_now !== false) {
      setIsAsap(true);
      setIsReturnTrip(false);
      setPassengerCount(1);
    }
  }, [location.state?.privateRideMode, rapidPilotEligible, productStatus?.ride_now]);

  usePrivateRideInsightTracking({
    quote: instantQuote,
    marketId: analyticsMarketId,
    productCode: rapidPilotRideNow ? 'ride_now' : 'private_rides',
    isAsap,
    isReturnTrip,
    returnMode,
    campaignCode: null,
  });

  const trackPrivateBookingEvent = (
    eventName,
    reasonCode = null,
    bookingId = null,
  ) =>
    trackBookingFunnelEvent(eventName, {
      reasonCode,
      rideMode: rapidPilotRideNow ? 'ride_now' : 'private',
      pageSource: rapidPilotRideNow ? 'ride_now_booking' : 'private_ride_booking',
      marketId: analyticsMarketId,
      bookingId,
      sessionId: privateRideJourneySessionIdRef.current,
    });

  useEffect(() => {
    if (!user?.id || !rapidPilotRideNow) return;
    // The same route hosts both scheduled and Ride Now. Record a separate
    // entry once the rider actually selects the on-demand product.
    void trackBookingFunnelEvent('booking_page_opened', {
      rideMode: 'ride_now',
      pageSource: 'ride_now_booking',
      marketId: analyticsMarketId,
      sessionId: privateRideJourneySessionIdRef.current,
    });
  }, [analyticsMarketId, rapidPilotRideNow, user?.id]);

  useEffect(() => {
    if (!user?.id || analyticsPageOpenedRef.current) return;
    // A direct Ride Now entry is the same route, but NOT a scheduled
    // Private Ride visit. The Ride Now effect records its own page entry
    // once the pilot eligibility check finishes.
    if (location.state?.privateRideMode === 'ride_now') return;

    analyticsPageOpenedRef.current = true;
    void trackPrivateBookingEvent('booking_page_opened');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.market_id]);

  const earliestScheduledDateTime = useMemo(
    () => addMinutes(new Date(), minimumNoticeMinutes),
    [minimumNoticeMinutes],
  );

  const minScheduledDate = useMemo(
    () => toDateInput(earliestScheduledDateTime),
    [earliestScheduledDateTime],
  );

  const maxBookingDate = useMemo(
    () =>
      toDateInput(addDays(new Date(), Number(settings.max_advance_days || 30))),
    [settings.max_advance_days],
  );

  const minPickupTimeForSelectedDate = useMemo(
    () => toTimeInput(roundUpToQuarterHour(earliestScheduledDateTime)),
    [earliestScheduledDateTime],
  );

  const effectivePickupDate = isAsap ? toDateInput(new Date()) : bookingDate;
  const effectivePickupTime = isAsap
    ? toTimeInput(roundUpToQuarterHour(new Date()))
    : pickupTime;

  const calculatedWaitingReturn = useMemo(() => {
    if (!isReturnTrip || returnMode !== "driver_waits") return null;

    if (instantQuote?.return_departure?.date && instantQuote?.return_departure?.time) {
      return {
        date: instantQuote.return_departure.date,
        time: instantQuote.return_departure.time,
      };
    }

    return null;
  }, [
    isReturnTrip,
    returnMode,
    instantQuote?.return_departure?.date,
    instantQuote?.return_departure?.time,
  ]);

  const instantQuoteSummary = useMemo(() => {
    if (!instantQuote?.quote_id) return null;

    return {
      total: Number(instantQuote.passenger_total_price || 0),
      distanceKm: Number(instantQuote.distance_km || 0),
      durationMinutes: Number(
        instantQuote.route_duration_minutes ||
          instantQuote.estimated_duration_minutes ||
          0,
      ),
      operationalBlockMinutes: Number(
        instantQuote.operational_block_minutes || 0,
      ),
      expiresAt: instantQuote.expires_at || null,
      lateNightPricingApplied:
        instantQuote.late_night_pricing_applied === true,
      recoveryPricingApplied:
        instantQuote.recovery_pricing_applied === true,
      waitingPricingApplied:
        instantQuote.waiting_pricing_applied === true,
      groupRide: instantQuote.group_ride === true,
      vehicleCount: Number(instantQuote.vehicle_count || 1),
      perPersonPrice: Number(instantQuote.per_person_price || 0),
      passengersPerVehicle: Array.isArray(instantQuote.passengers_per_vehicle)
        ? instantQuote.passengers_per_vehicle
        : [],
    };
  }, [instantQuote]);

  const airportTransferRequired =
    instantQuoteNotice?.code === "AIRPORT_TRANSFER_REQUIRED";
  const manualQuoteRequired =
    groupQuoteRequired ||
    instantQuoteNotice?.code === "MANUAL_REVIEW_REQUIRED";
  const instantQuoteReady = Boolean(instantQuote?.quote_id);

  const customRouteMode =
    requestResult?.status === "awaiting_admin_quote";
  const submittedGroupRide = requestResult?.group_ride === true;
  const submittedInstantGroupRide =
    submittedGroupRide && requestResult?.instant_quote === true;
  const submittedManualGroupRide =
    submittedGroupRide && requestResult?.instant_quote !== true;

  // Network Dispatch resolves the fulfilment market from the exact pickup and
  // destination. Both can therefore be searched across South Africa. Airport
  // access areas remain protected and redirect the passenger to Airport Transfers.
  const pickupSearchScope = "south_africa";
  const destinationSearchScope = "south_africa";

  const clearInstantQuote = () => {
    setInstantQuote(null);
    setInstantQuoteError("");
    setInstantQuoteNotice(null);
    setInstantQuoteLoading(false);
  };

  const handleAsapChange = (enabled) => {
    if (enabled && productStatus?.ride_now === false) {
      setPageError('Ride Now is temporarily unavailable. Please choose Schedule.');
      return;
    }
    if (!enabled && productStatus?.scheduled_private === false) {
      setPageError('Scheduled Private Rides are temporarily unavailable.');
      return;
    }
    setPageError('');
    if (enabled) {
      setReturnMode('separate_return');
      // Switching from a scheduled Group Ride must not carry 5+ passengers into Ride Now.
      setPassengerCount((current) =>
        Math.max(1, Math.min(STANDARD_PRIVATE_RIDE_MAX_PASSENGERS, Number(current) || 1)),
      );
    }
    setIsAsap(enabled);
    void trackPrivateBookingEvent('travel_date_selected');

    if (enabled) {
      const now = roundUpToQuarterHour(new Date());
      setBookingDate(toDateInput(now));
      setPickupTime(toTimeInput(now));
      return;
    }

    const minimumDateTime = addMinutes(
      roundUpToQuarterHour(new Date()),
      minimumNoticeMinutes,
    );
    setBookingDate(toDateInput(minimumDateTime));
    setPickupTime(toTimeInput(minimumDateTime));
  };

  const openPrivateLocationPicker = (locationType) => {
    void trackPrivateBookingEvent(
      locationType === 'pickup'
        ? 'pickup_location_started'
        : 'dropoff_location_started',
    );
    setActiveLocationPicker(locationType);
  };

  const handlePrivateTravelDateChange = (nextDate) => {
    setBookingDate(nextDate);

    if (
      isReturnTrip &&
      returnMode === "separate_return" &&
      returnDate &&
      returnDate < nextDate
    ) {
      setReturnDate(nextDate);
    }

    void trackPrivateBookingEvent('travel_date_selected');
  };

  const handlePrivatePickupTimeChange = (nextTime) => {
    let safeTime = nextTime;

    if (
      bookingDate === minScheduledDate &&
      minPickupTimeForSelectedDate &&
      safeTime < minPickupTimeForSelectedDate
    ) {
      safeTime = minPickupTimeForSelectedDate;
    }

    setPickupTime(safeTime);

    if (
      isReturnTrip &&
      returnMode === "separate_return" &&
      returnDate === bookingDate &&
      returnTime &&
      safeTime &&
      returnTime <= safeTime
    ) {
      const nextReturnDate = toDateInput(
        addDays(new Date(`${bookingDate}T12:00:00`), 1),
      );

      if (nextReturnDate <= maxBookingDate) {
        setReturnDate(nextReturnDate);
      }
    }
  };

  const handlePrivateReturnTimeChange = (nextTime) => {
    setReturnTime(nextTime);

    if (
      !nextTime ||
      !effectivePickupDate ||
      !effectivePickupTime ||
      returnDate !== effectivePickupDate
    ) {
      return;
    }

    const pickupDateTime = buildLocalDateTime(
      effectivePickupDate,
      effectivePickupTime,
    );
    const returnDateTime = buildLocalDateTime(returnDate, nextTime);

    if (pickupDateTime && returnDateTime && returnDateTime <= pickupDateTime) {
      const nextReturnDate = toDateInput(
        addDays(new Date(`${effectivePickupDate}T12:00:00`), 1),
      );

      if (nextReturnDate <= maxBookingDate) {
        setReturnDate(nextReturnDate);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadBookingOptions = async () => {
      try {
        setOptionsLoading(true);
        setPageError("");

        const { data, error } = await supabase.rpc(
          "get_my_private_ride_booking_options_v1",
        );

        if (error) throw error;

        if (!cancelled) {
          setBookingOptions(data || null);

          const minimumDateTime = addMinutes(
            roundUpToQuarterHour(new Date()),
            Math.max(
              0,
              Number(data?.settings?.minimum_notice_minutes ?? 30),
            ),
          );
          const plannedReturn = buildLocalDateTime(
            scheduledReturnDraft?.date, scheduledReturnDraft?.time,
          );
          const withinScheduleWindow = plannedReturn &&
            plannedReturn >= minimumDateTime &&
            plannedReturn <= addDays(new Date(), Number(data?.settings?.max_advance_days || 30));
          setBookingDate(withinScheduleWindow ? scheduledReturnDraft.date : toDateInput(minimumDateTime));
          setPickupTime(withinScheduleWindow ? scheduledReturnDraft.time : toTimeInput(minimumDateTime));
          setReturnDate(toDateInput(minimumDateTime));
        }
      } catch (error) {
        console.error("Private Ride options failed:", error);
        void trackPrivateBookingEvent(
          'booking_validation_failed',
          inferBookingFunnelReasonCode(error, 'NETWORK_ERROR'),
        );

        if (!cancelled) {
          setPageError(
            error?.message ||
              "Could not load Private Ride options for your market.",
          );
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };

    loadBookingOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadGroupRideOptions = async () => {
      try {
        const { data, error } = await supabase.rpc(
          "get_my_group_ride_options_v1",
        );
        if (error) throw error;
        if (!cancelled) setGroupRideOptions(data || null);
      } catch (error) {
        console.error("Group Ride options failed:", error);
        if (!cancelled) {
          setGroupRideOptions({
            enabled: false,
            max_group_passengers: 5,
            message: "Group Ride options could not be loaded. Please try again.",
          });
        }
      }
    };

    void loadGroupRideOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // A changed journey must cancel its obsolete price request.
    const quoteAbortController = new AbortController();

    if (
      groupQuoteRequired ||
      additionalStops.some((stop) => !stop || stop.latitude == null || stop.longitude == null || !Number.isFinite(Number(stop.latitude)) || !Number.isFinite(Number(stop.longitude))) ||
      !pickupPoint?.latitude ||
      !pickupPoint?.longitude ||
      !dropoffPoint?.latitude ||
      !dropoffPoint?.longitude ||
      !effectivePickupDate ||
      !effectivePickupTime ||
      (isReturnTrip &&
        returnMode === "separate_return" &&
        (!returnDate || !returnTime))
    ) {
      clearInstantQuote();
      return undefined;
    }

    // Invalidate the previous price immediately when the journey changes.
    // Never allow a stale quote to remain selectable during the debounce.
    setInstantQuote(null);
    setInstantQuoteLoading(true);
    setInstantQuoteError("");
    setInstantQuoteNotice(null);

    const timer = window.setTimeout(async () => {
      try {
        setInstantQuoteLoading(true);
        setInstantQuoteError("");
        setInstantQuoteNotice(null);

        const { data, error } = await supabase.functions.invoke(
          "private-ride-instant-quote",
          {
            signal: quoteAbortController.signal,
            body: {
              action: "quote",
              pickup: {
                label: pickupPoint.label || pickupLocation,
                latitude: Number(pickupPoint.latitude),
                longitude: Number(pickupPoint.longitude),
              },
              dropoff: {
                label: dropoffPoint.label || dropoffLocation,
                latitude: Number(dropoffPoint.latitude),
                longitude: Number(dropoffPoint.longitude),
              },
              stops: additionalStops.map((point) => ({ label: point.label || point.formattedAddress, latitude: Number(point.latitude), longitude: Number(point.longitude) })),
              booking_date: effectivePickupDate,
              pickup_time: effectivePickupTime,
              passenger_count: Number(passengerCount),
              is_asap: isAsap,
              is_return_trip: isReturnTrip && !rideNowReturnLater,
              return_mode: isReturnTrip && !rideNowReturnLater ? returnMode : null,
              return_date:
                isReturnTrip && !rideNowReturnLater && returnMode === "separate_return"
                  ? returnDate
                  : null,
              return_time:
                isReturnTrip && !rideNowReturnLater && returnMode === "separate_return"
                  ? returnTime
                  : null,
              waiting_hours:
                isReturnTrip && !rideNowReturnLater && returnMode === "driver_waits"
                  ? Number(waitingHours || 0)
                  : 0,
            },
          },
        );

        if (error) {
          const functionError =
            error?.context && typeof error.context.json === "function"
              ? await error.context.json().catch(() => null)
              : null;

          throw new Error(
            functionError?.message ||
              functionError?.code ||
              error?.message ||
              "We could not calculate your Private Ride fare.",
          );
        }

        if (!data?.ok) {
          const responseCode = String(data?.code || '').toUpperCase();

          if (responseCode === 'MANUAL_REVIEW_REQUIRED') {
            // A manual-quote notice is still normal Private Ride browsing.
            // Recovery only starts after the passenger submits a request or
            // reaches a genuine supply/payment block.
          } else if (responseCode === 'AIRPORT_TRANSFER_REQUIRED') {
            void trackPrivateBookingEvent(
              'booking_validation_failed',
              'AIRPORT_TRANSFER_REQUIRED',
            );
          } else {
            const noAvailabilityReasonCode =
              responseCode || 'NO_AVAILABILITY';
            const shouldCreateImmediateRecovery =
              noAvailabilityReasonCode.includes('NO_DRIVER') ||
              noAvailabilityReasonCode.includes('NO_RIDE') ||
              noAvailabilityReasonCode.includes('NO_AVAILABILITY');

            // Save the normal funnel event first. The detailed recovery
            // snapshot is written afterwards so it remains the latest source
            // of truth for the Business Command Centre.
            try {
              await trackPrivateBookingEvent(
                'no_availability_found',
                noAvailabilityReasonCode,
              );
            } catch (trackingError) {
              console.warn(
                '[Journey Recovery] Standard Private Ride no-availability tracking failed:',
                trackingError?.message || trackingError,
              );
            }

            if (shouldCreateImmediateRecovery) {
              const origin = compactRecoveryLocation(
                pickupPoint,
                pickupLocation,
              );
              const destination = compactRecoveryLocation(
                dropoffPoint,
                dropoffLocation,
              );

              await recordImmediateNoAvailabilityRecovery({
                marketId: analyticsMarketId,
                productCode: rapidPilotRideNow ? 'ride_now' : 'private_rides',
                userId: user?.id,
                sessionKey: privateRideJourneySessionIdRef.current,
                reasonCode: noAvailabilityReasonCode,
                pageSource: 'private_ride_booking',
                journeyContext: {
                  booking_date: effectivePickupDate,
                  requested_date: effectivePickupDate,
                  requested_time: effectivePickupTime,
                  pickup_time: effectivePickupTime,
                  is_asap: Boolean(isAsap),
                  trip_direction: 'private_ride',
                  route_label: `${origin?.label || 'Pickup'} → ${destination?.label || 'Destination'}`,
                  origin,
                  destination,
                  passenger_count: Number(passengerCount || 1),
                  is_return_trip: Boolean(isReturnTrip),
                  return_mode: isReturnTrip ? returnMode : null,
                  return_date:
                    isReturnTrip && returnMode === 'separate_return'
                      ? returnDate
                      : null,
                  return_time:
                    isReturnTrip && returnMode === 'separate_return'
                      ? returnTime
                      : null,
                  waiting_hours:
                    isReturnTrip && returnMode === 'driver_waits'
                      ? Number(waitingHours || 0)
                      : 0,
                  response_message: data?.message || null,
                  reason_code: noAvailabilityReasonCode,
                },
              });
            }

          }

          if (!cancelled) {
            setInstantQuote(null);
            setInstantQuoteNotice(data || null);
            setInstantQuoteError(data?.message || "We could not calculate an instant quote for this journey.");
          }
          return;
        }

        if (!cancelled) {
          setInstantQuote(data);
          setInstantQuoteNotice(null);
          setInstantQuoteError("");

          if (data?.quote_id && lastTrackedQuoteIdRef.current !== data.quote_id) {
            lastTrackedQuoteIdRef.current = data.quote_id;
            void trackPrivateBookingEvent('fare_shown');
          }
        }
      } catch (error) {
        // Editing a journey cancels its previous quote; do not record an
        // artificial booking error or replace a newer valid price.
        if (cancelled || quoteAbortController.signal.aborted) return;
        console.error("Private Ride instant quote failed:", error);
        void trackPrivateBookingEvent(
          'booking_validation_failed',
          inferBookingFunnelReasonCode(error, 'QUOTE_FAILED'),
        );

        if (!cancelled) {
          setInstantQuote(null);
          setInstantQuoteNotice(null);
          setInstantQuoteError(
            error?.message || "We could not calculate an instant quote for this journey.",
          );
        }
      } finally {
        if (!cancelled) setInstantQuoteLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      quoteAbortController.abort();
    };
  }, [
    additionalStops,
    pickupPoint?.label,
    pickupPoint?.latitude,
    pickupPoint?.longitude,
    dropoffPoint?.label,
    dropoffPoint?.latitude,
    dropoffPoint?.longitude,
    pickupLocation,
    dropoffLocation,
    effectivePickupDate,
    effectivePickupTime,
    passengerCount,
    isAsap,
    isReturnTrip,
    returnMode,
    returnDate,
    returnTime,
    waitingHours,
    isGroupRide,
  ]);

  const failPrivateValidation = (message, reasonCode) => {
    setPageError(message);
    void trackPrivateBookingEvent('booking_validation_failed', reasonCode);
    return false;
  };

  const validateBeforeSubmit = () => {
    setPageError("");

    if (isAsap && isReturnTrip && (!rapidPilotEligible || returnMode !== 'separate_return')) {
      return failPrivateValidation(
        "Ride Now returns must be booked separately. Choose Return later or switch to Schedule.",
        "RIDE_NOW_RETURN_MODE_UNSUPPORTED",
      );
    }

    if (isAsap && manualQuoteRequired) {
      return failPrivateValidation(
        "This route needs manual review and cannot be booked through Ride Now. Select Schedule Ahead to submit your request to UniAir operations.",
        "RIDE_NOW_MANUAL_REVIEW_REQUIRED",
      );
    }

    if (rideNowReturnLater && (manualQuoteRequired || !instantQuoteReady)) {
      return failPrivateValidation(
        "This route cannot use instant Ride Now quoting. Choose Schedule to request your journey and return.",
        "RIDE_NOW_RETURN_REQUIRES_INSTANT_OUTWARD_QUOTE",
      );
    }

    if (rideNowReturnLater) {
      const plannedReturn = buildLocalDateTime(returnDate, returnTime);
      if (!plannedReturn || plannedReturn < addMinutes(new Date(), minimumNoticeMinutes)) {
        return failPrivateValidation(
          `Choose a return pickup at least ${minimumNoticeLabel} from now.`,
          "RIDE_NOW_RETURN_NOTICE_REQUIRED",
        );
      }
    }

    if (isAsap && (
      !Number.isInteger(Number(passengerCount)) ||
      Number(passengerCount) < 1 ||
      Number(passengerCount) > STANDARD_PRIVATE_RIDE_MAX_PASSENGERS
    )) {
      return failPrivateValidation(
        "Ride Now supports up to 4 passengers in one car. Choose Schedule for larger groups.",
        "RIDE_NOW_PASSENGER_LIMIT",
      );
    }

    if (additionalStops.length > 2 || additionalStops.some(stop =>
      !stop || stop.latitude == null || stop.longitude == null || !Number.isFinite(Number(stop.latitude)) || !Number.isFinite(Number(stop.longitude))
    )) {
      return failPrivateValidation("Choose up to two valid additional stops.", "INVALID_ADDITIONAL_STOPS");
    }
    if (additionalStops.length > 0 && ((isReturnTrip && !rideNowReturnLater) || Number(passengerCount) > 4)) {
      return failPrivateValidation("Additional stops are currently available for one-way, single-car rides only.", "MULTI_STOP_UNSUPPORTED_RIDE");
    }
    if (additionalStops.length > 0 && (!instantQuoteReady || instantQuoteLoading)) {
      return failPrivateValidation("Wait for the verified multi-stop quote before continuing.", "MULTI_STOP_QUOTE_PENDING");
    }

    if (isBlank(pickupLocation)) {
      return failPrivateValidation(
        "Please enter your exact pickup location.",
        "MISSING_PICKUP_LOCATION",
      );
    }

    if (isBlank(dropoffLocation)) {
      return failPrivateValidation(
        "Please enter your exact destination.",
        "MISSING_DROPOFF_LOCATION",
      );
    }

    if (!pickupPoint?.latitude || !pickupPoint?.longitude) {
      return failPrivateValidation(
        "Please select the exact pickup location.",
        "INVALID_PICKUP_LOCATION",
      );
    }

    if (!dropoffPoint?.latitude || !dropoffPoint?.longitude) {
      return failPrivateValidation(
        "Please select the exact destination.",
        "INVALID_DROPOFF_LOCATION",
      );
    }

    if (!isAsap) {
      const pickupDateTime = buildLocalDateTime(bookingDate, pickupTime);

      if (!pickupDateTime) {
        return failPrivateValidation(
          "Choose a valid pickup date and time.",
          "INVALID_DATE_TIME",
        );
      }

      if (pickupDateTime < earliestScheduledDateTime) {
        return failPrivateValidation(
          `Scheduled Private Rides need to be booked at least ${minimumNoticeLabel} ahead.`,
          "MINIMUM_NOTICE_REQUIRED",
        );
      }
    }

    if (isReturnTrip && returnMode === "separate_return") {
      if (rideNowReturnLater && buildLocalDateTime(returnDate, returnTime) <
        addMinutes(new Date(), minimumNoticeMinutes)) {
        return failPrivateValidation(
          `Choose a return pickup at least ${minimumNoticeLabel} from now.`,
          "RIDE_NOW_RETURN_NOTICE_REQUIRED",
        );
      }
      const pickupDateTime = buildLocalDateTime(
        effectivePickupDate,
        effectivePickupTime,
      );
      const returnDateTime = buildLocalDateTime(returnDate, returnTime);

      if (!returnDateTime) {
        return failPrivateValidation(
          "Choose a valid return date and time.",
          "INVALID_RETURN_TIME",
        );
      }

      if (pickupDateTime && returnDateTime <= pickupDateTime) {
        return failPrivateValidation(
          "Your return pickup must be after your first pickup.",
          "INVALID_RETURN_TIME",
        );
      }
    }

    if (
      isReturnTrip &&
      returnMode === "driver_waits" &&
      (!Number.isInteger(Number(waitingHours)) ||
        Number(waitingHours) < 1 ||
        Number(waitingHours) > 4)
    ) {
      return failPrivateValidation(
        "Choose between 1 and 4 hours for driver waiting time.",
        "INVALID_WAITING_HOURS",
      );
    }

    if (isGroupRide && groupRideOptions?.enabled === false) {
      return failPrivateValidation(
        groupRideOptions?.message ||
          "Group Ride requests are not available in your market right now.",
        "GROUP_RIDES_DISABLED",
      );
    }

    if (Number(passengerCount) > maxGroupPassengers) {
      return failPrivateValidation(
        `Group Ride requests currently support up to ${maxGroupPassengers} passengers.`,
        "GROUP_PASSENGER_LIMIT",
      );
    }

    if (airportTransferRequired) {
      return failPrivateValidation(
        "Airport trips must be booked through UniAir Airport Transfers.",
        "AIRPORT_TRANSFER_REQUIRED",
      );
    }

    if (!manualQuoteRequired && !instantQuoteReady) {
      return failPrivateValidation(
        instantQuoteError ||
          "Confirm both exact locations so UniAir can calculate your instant quote.",
        instantQuoteError ? "FARE_UNAVAILABLE" : "INVALID_LOCATION",
      );
    }

    return true;
  };

  const moveToStep = (nextStep) => {
    setPageError("");
    setStep(nextStep);

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };

  const continueFromWhere = () => {
    setPageError("");

    if (isBlank(pickupLocation) || !pickupPoint?.latitude || !pickupPoint?.longitude) {
      return failPrivateValidation(
        "Please select your exact pickup location.",
        isBlank(pickupLocation)
          ? "MISSING_PICKUP_LOCATION"
          : "INVALID_PICKUP_LOCATION",
      );
    }

    if (isBlank(dropoffLocation) || !dropoffPoint?.latitude || !dropoffPoint?.longitude) {
      return failPrivateValidation(
        "Please select your exact destination.",
        isBlank(dropoffLocation)
          ? "MISSING_DROPOFF_LOCATION"
          : "INVALID_DROPOFF_LOCATION",
      );
    }

    moveToStep(2);
    return true;
  };

  const continueFromWhen = () => {
    setPageError("");

    if (!isAsap) {
      const pickupDateTime = buildLocalDateTime(bookingDate, pickupTime);

      if (!pickupDateTime) {
        return failPrivateValidation(
          "Choose a valid pickup date and time.",
          "INVALID_DATE_TIME",
        );
      }

      if (pickupDateTime < earliestScheduledDateTime) {
        return failPrivateValidation(
          `Scheduled Private Rides need to be booked at least ${minimumNoticeLabel} ahead.`,
          "MINIMUM_NOTICE_REQUIRED",
        );
      }
    }

    if (isReturnTrip && returnMode === "separate_return") {
      if (rideNowReturnLater && buildLocalDateTime(returnDate, returnTime) <
        addMinutes(new Date(), minimumNoticeMinutes)) {
        return failPrivateValidation(
          `Choose a return pickup at least ${minimumNoticeLabel} from now.`,
          "RIDE_NOW_RETURN_NOTICE_REQUIRED",
        );
      }
      const pickupDateTime = buildLocalDateTime(
        effectivePickupDate,
        effectivePickupTime,
      );
      const returnDateTime = buildLocalDateTime(returnDate, returnTime);

      if (!returnDateTime || (pickupDateTime && returnDateTime <= pickupDateTime)) {
        return failPrivateValidation(
          "Your return pickup must be after your first pickup.",
          "INVALID_RETURN_TIME",
        );
      }
    }

    if (
      isReturnTrip &&
      returnMode === "driver_waits" &&
      (!Number.isInteger(Number(waitingHours)) ||
        Number(waitingHours) < 1 ||
        Number(waitingHours) > 4)
    ) {
      return failPrivateValidation(
        "Choose between 1 and 4 hours for driver waiting time.",
        "INVALID_WAITING_HOURS",
      );
    }

    moveToStep(3);
    return true;
  };

  const handleHeaderBack = () => {
    if (step > 1) {
      moveToStep(step - 1);
      return;
    }

    navigate("/dashboard");
  };

  const submitRequest = async (event) => {
    event.preventDefault();

    if (submitting) return;

    void trackPrivateBookingEvent('booking_submit_clicked');
    if (!validateBeforeSubmit()) return;

    try {
      setSubmitting(true);
      setPageError("");

      let data;
      let error;

      if (groupQuoteRequired) {
        const requestPickupDate = isAsap ? toDateInput(new Date()) : bookingDate;
        const requestPickupTime = isAsap
          ? toTimeInput(roundUpToQuarterHour(new Date()))
          : pickupTime;

        const response = await supabase.rpc(
          "create_my_group_ride_request_v1",
          {
            p_pickup_location: pickupLocation.trim(),
            p_dropoff_location: dropoffLocation.trim(),
            p_booking_date: requestPickupDate,
            p_pickup_time: requestPickupTime,
            p_passenger_count: Number(passengerCount),
            p_is_asap: isAsap,
            p_is_return_trip: isReturnTrip,
            p_return_mode: isReturnTrip ? returnMode : null,
            p_return_date:
              isReturnTrip && returnMode === "separate_return"
                ? returnDate
                : calculatedWaitingReturn?.date || null,
            p_return_time:
              isReturnTrip && returnMode === "separate_return"
                ? returnTime
                : calculatedWaitingReturn?.time || null,
            p_waiting_hours:
              isReturnTrip && returnMode === "driver_waits"
                ? Number(waitingHours)
                : 0,
            p_notes: notes.trim() || null,
            p_pickup_label: pickupPoint.label,
            p_pickup_latitude: Number(pickupPoint.latitude),
            p_pickup_longitude: Number(pickupPoint.longitude),
            p_dropoff_label: dropoffPoint.label,
            p_dropoff_latitude: Number(dropoffPoint.latitude),
            p_dropoff_longitude: Number(dropoffPoint.longitude),
          },
        );
        data = response.data;
        error = response.error;
      } else if (instantQuoteReady) {
        const response = await supabase.rpc(
          "create_my_instant_private_ride_request_v3",
          {
            p_quote_id: instantQuote.quote_id,
            p_notes: notes.trim() || null,
          },
        );
        data = response.data;
        error = response.error;
      } else {
        const requestPickupDate = isAsap ? toDateInput(new Date()) : bookingDate;
        const requestPickupTime = isAsap
          ? toTimeInput(roundUpToQuarterHour(new Date()))
          : pickupTime;

        const response = await supabase.rpc(
          "create_my_private_ride_request_with_navigation_v2",
          {
            p_route_price_id: null,
            p_pickup_location: pickupLocation.trim(),
            p_dropoff_location: dropoffLocation.trim(),
            p_booking_date: requestPickupDate,
            p_pickup_time: requestPickupTime,
            p_passenger_count: Number(passengerCount),
            p_is_asap: isAsap,
            p_is_return_trip: isReturnTrip,
            p_return_mode: isReturnTrip ? returnMode : null,
            p_return_date:
              isReturnTrip && returnMode === "separate_return"
                ? returnDate
                : calculatedWaitingReturn?.date || null,
            p_return_time:
              isReturnTrip && returnMode === "separate_return"
                ? returnTime
                : calculatedWaitingReturn?.time || null,
            p_waiting_hours:
              isReturnTrip && returnMode === "driver_waits"
                ? Number(waitingHours)
                : 0,
            p_notes: notes.trim() || null,
            p_pickup_label: pickupPoint.label,
            p_pickup_latitude: Number(pickupPoint.latitude),
            p_pickup_longitude: Number(pickupPoint.longitude),
            p_dropoff_label: dropoffPoint.label,
            p_dropoff_latitude: Number(dropoffPoint.latitude),
            p_dropoff_longitude: Number(dropoffPoint.longitude),
          },
        );
        data = response.data;
        error = response.error;
      }

      if (error) throw error;

      if (data?.ok && location.state?.returnSourceTripId && user?.id) {
        // Only clear the reminder after the scheduled return request exists.
        try {
          window.sessionStorage.removeItem(
            `uniair:rapid-return:${user.id}:${location.state.returnSourceTripId}`,
          );
        } catch { /* Optional browser storage. */ }
      }

      const recoveredEntityId =
        data?.request_group_id ||
        data?.private_ride_request_id ||
        data?.request_id ||
        data?.booking_id ||
        data?.id ||
        null;

      void trackPrivateBookingEvent(rapidPilotRideNow ? 'ride_requested' : 'booking_created');
      if (!rapidPilotRideNow) {
        // A Ride Now request is NOT a successful booking until a driver
        // accepts. Scheduled Private Rides retain their existing semantics.
        void resolveMyJourneyRecoveryAfterBooking({
          productCode: 'private_rides',
          entityType: isGroupRide ? 'group_ride_request' : 'private_ride_request',
          entityId: recoveredEntityId,
        });
      }
      const rapidCandidate = isAsap && (!isReturnTrip || rideNowReturnLater) && !isGroupRide &&
        instantQuoteReady && Boolean(data?.request_id) &&
        /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(String(data.request_id));
      if (rapidCandidate) {
        const dispatchMarketId = data?.dispatch_context?.dispatch_market_id ||
          analyticsMarketId;
        if (dispatchMarketId) {
          const { data: pilot } = await supabase.rpc('uniair_rapid_my_pilot_eligibility_v1', {
            p_market_id: dispatchMarketId,
          });
          if (pilot?.enabled === true) {
            if (rideNowReturnLater) {
              // The return is a draft until a second, explicit scheduled checkout.
              try {
                window.sessionStorage.setItem(
                  `uniair:rapid-return:${user.id}:${data.request_id}`,
                  JSON.stringify({
                    date: returnDate,
                    time: returnTime,
                    passengerCount: Number(passengerCount),
                    pickupPoint: { ...dropoffPoint, label: dropoffPoint.label || dropoffPoint.formattedAddress || dropoffLocation },
                    dropoffPoint: { ...pickupPoint, label: pickupPoint.label || pickupPoint.formattedAddress || pickupLocation },
                  }),
                );
              } catch { /* Booking works without optional browser storage. */ }
            }
            // Present the fare, promo and card choice in one short payment
            // sheet. Only its final confirmation starts a Paystack hold.
            // Restored sessions must never start another hold automatically.
            setRapidAutoBegin(false);
            try {
              window.sessionStorage.setItem(
                `uniair:rapid:journey:${data.request_id}`,
                privateRideJourneySessionIdRef.current,
              );
            } catch { /* Analytics never blocks a booking. */ }
            setRapidRequestId(data.request_id);
            try {
              window.sessionStorage.setItem(
                `uniair-rapid-request:${user.id}`, String(data.request_id));
            } catch { /* Optional tab-local recovery. */ }
          }
        }
      }
      setRequestResult(data || { ok: true });
    } catch (error) {
      console.error("Private Ride request failed:", error);
      void trackPrivateBookingEvent(
        'booking_validation_failed',
        inferBookingFunnelReasonCode(error, 'REQUEST_FAILED'),
      );
      setPageError(
        error?.message || "Could not send your Private Ride request.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (rapidRequestId) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8" style={UNIAIR_PREMIUM_TYPOGRAPHY}>
        <main className="mx-auto max-w-xl">
          <RapidRideNowPayment requestId={rapidRequestId}
            marketId={analyticsMarketId}
            journeySessionId={privateRideJourneySessionIdRef.current}
            autoBegin={rapidAutoBegin}
            autoVerifyOnReturn={new URLSearchParams(location.search).get("rapid_return") === "1"}
            onRetry={() => {
              try {
                window.sessionStorage.removeItem(`uniair:rapid-return:${user?.id}:${rapidRequestId}`);
                window.sessionStorage.removeItem(`uniair-rapid-request:${user?.id}`);
              }
              catch { /* No card data stored in this session. */ }
              setRapidRequestId(null);
              setRapidAutoBegin(false);
              if (location.search) navigate("/private-rides", { replace: true });
              setRequestResult(null);
              setInstantQuote(null);
              setInstantQuoteError("");
              setIsAsap(true);
              setIsReturnTrip(false);
              setPassengerCount(1);
              setStep(1);
            }}
            onScheduleInstead={() => {
              // This action is only enabled when the earlier Ride Now hold
              // is confirmed released (or authorization never began).
              try {
                window.sessionStorage.removeItem(`uniair:rapid-return:${user?.id}:${rapidRequestId}`);
                window.sessionStorage.removeItem(`uniair-rapid-request:${user?.id}`);
              }
              catch {/* Optional session recovery. */}
              setRapidRequestId(null);
              setRapidAutoBegin(false);
              setRequestResult(null);
              setInstantQuote(null);
              setInstantQuoteError('');
              setIsAsap(false);
              setIsReturnTrip(false);
              const nextPickup=roundUpToQuarterHour(
                addMinutes(new Date(),Math.max(30,minimumNoticeMinutes)));
              setBookingDate(toDateInput(nextPickup));
              setPickupTime(toTimeInput(nextPickup));
              setStep(1);
              // Retain the pickup, destination, locations and passenger count.
              navigate('/private-rides',{replace:true,
                state:{privateRideMode:'scheduled'}});
            }}
            onLeave={() => {
              // Navigation is independent of Paystack reconciliation. Clearing
              // this *browser pointer* never alters an actual card hold.
              try { window.sessionStorage.removeItem(`uniair-rapid-request:${user?.id}`); }
              catch { /* Browser storage is optional. */ }
              setRapidRequestId(null);
              setRapidAutoBegin(false);
              navigate('/dashboard', { replace: true });
            }}
            onFinished={(privateTripId) => {
              if (!privateTripId) return;
              try {
                const returnDraftKey = `uniair:rapid-return:${user?.id}:${rapidRequestId}`;
                const draft = window.sessionStorage.getItem(returnDraftKey);
                if (draft) {
                  window.sessionStorage.setItem(`uniair:rapid-return:${user?.id}:${privateTripId}`, draft);
                  window.sessionStorage.removeItem(returnDraftKey);
                }
                window.sessionStorage.removeItem(`uniair-rapid-request:${user?.id}`);
              }
              catch { /* Optional session persistence. */ }
              setRapidRequestId(null);
              setRapidAutoBegin(false);
              // Open the confirmed trip itself, never restart the booking form.
              navigate(`/ride-now/trip/${encodeURIComponent(privateTripId)}`, { replace: true });
            }}
          />
        </main>
      </div>
    );
  }

  if (requestResult?.ok) {
    return (
      <div className="min-h-screen bg-gray-50" style={UNIAIR_PREMIUM_TYPOGRAPHY}>
        <header className="sticky top-0 z-30 bg-[#720e1e] text-white shadow-lg">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl font-bold hover:bg-white/20"
              aria-label="Back to dashboard"
            >
              ←
            </button>

            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.11em] text-white/70">
                UniAir
              </p>
              <h1 className="text-lg font-bold">Private Rides</h1>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-xl px-4 py-8">
          <section className="rounded-3xl border border-green-200 bg-white p-6 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl font-bold text-green-700">
              ✓
            </div>

            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              {submittedInstantGroupRide
                ? "Group Ride request sent to drivers"
                : submittedManualGroupRide
                  ? "Group Ride request received"
                  : customRouteMode
                    ? "Request received"
                    : "Request sent to drivers"}
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-600">
              {submittedInstantGroupRide
                ? "Your two-car Group Ride has been sent to eligible drivers. Payment will open once both required vehicle slots are covered."
                : submittedManualGroupRide
                  ? "UniAir will confirm the appropriate vehicle plan and send your quote. After payment, your booking is confirmed and driver details will be shared once assigned."
                  : customRouteMode
                    ? "UniAir will review the route and send you a quote before your request is shared with drivers."
                    : "Eligible drivers have been notified. You will receive an email and app notification when every required ride leg has been accepted."}
            </p>

            <div className="mt-6 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-left">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                Pickup
              </p>
              <p className="mt-1 font-bold text-gray-900">
                {isAsap
                  ? "As soon as an eligible driver can accept"
                  : `${formatDate(bookingDate)} · ${formatTime(pickupTime)}`}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {pickupLocation} → {dropoffLocation}
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="mt-6 w-full rounded-2xl bg-[#720e1e] py-3.5 font-bold text-white hover:bg-[#5a0b18]"
            >
              Back to dashboard
            </button>

            <button
              type="button"
              onClick={() => {
                setRequestResult(null);
                setPickupLocation("");
                setDropoffLocation("");
                setPickupPoint(null);
                setDropoffPoint(null);
                setNotes("");
                setStep(1);
                clearInstantQuote();
              }}
              className="mt-3 w-full rounded-2xl border border-gray-300 py-3.5 font-bold text-gray-700 hover:bg-gray-50"
            >
              Create another request
            </button>
          </section>
        </main>
      </div>
    );
  }

  if (productStatus && (
    (isAsap && productStatus.ride_now === false) ||
    (!isAsap && productStatus.scheduled_private === false)
  )) {
    const alternateAvailable = isAsap
      ? productStatus.scheduled_private !== false
      : productStatus.ride_now !== false && rapidPilotEligible;
    return (
      <main className="min-h-screen bg-[#f5f5f5] px-4 py-12" style={UNIAIR_PREMIUM_TYPOGRAPHY}>
        <section className="mx-auto max-w-md rounded-3xl border border-gray-200 bg-white p-7 text-center shadow-sm">
          <h1 className="text-xl font-bold text-gray-950">
            {isAsap ? 'Ride Now' : 'Scheduled Private Rides'} temporarily unavailable
          </h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">This booking option is not accepting new requests right now. Any existing rides are still available in My Rides.</p>
          {alternateAvailable && (
            <button type="button"
              onClick={() => handleAsapChange(!isAsap)}
              className="mt-6 w-full rounded-xl bg-[#720e1e] px-4 py-3 font-semibold text-white">
              {isAsap ? 'Schedule a ride instead' : 'Request Ride Now instead'}
            </button>
          )}
          <button type="button" onClick={() => navigate('/dashboard')}
            className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 font-semibold text-gray-700">Back to dashboard</button>
        </section>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] pb-28" style={UNIAIR_PREMIUM_TYPOGRAPHY}>
      <header className="sticky top-0 z-30 border-b border-black/5 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={handleHeaderBack}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xl font-semibold text-gray-900 transition hover:bg-gray-200"
            aria-label={step > 1 ? "Go to previous step" : "Back to dashboard"}
          >
            ←
          </button>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#720e1e]">
              {rapidPilotRideNow ? "UniAir Ride Now" : "UniAir Private Rides"}
            </p>
            <h1 className="truncate text-lg font-bold text-gray-950">
              {step === 1
                ? (rapidPilotRideNow ? "Where to?" : "Where are you going?")
                : step === 2
                  ? (rapidPilotRideNow ? "Your ride details" : "When do you need the ride?")
                  : (rapidPilotRideNow ? "Review your Ride Now" : "Review your ride")}
            </h1>
          </div>

          <span className="rounded-full bg-[#720e1e]/10 px-3 py-1.5 text-xs font-bold text-[#720e1e]">
            {step}/3
          </span>
        </div>

        <div className="mx-auto grid max-w-xl grid-cols-3 gap-1 px-4 pb-3" aria-label="Booking progress">
          {[1, 2, 3].map((progressStep) => (
            <span
              key={progressStep}
              className={`h-1 rounded-full transition ${progressStep <= step ? "bg-[#720e1e]" : "bg-gray-200"}`}
            />
          ))}
        </div>
      </header>

      <main className={step === 1 ? "mx-auto max-w-xl px-0 pb-4 pt-0 sm:px-4 sm:pt-5" : "mx-auto max-w-xl px-4 py-5"}>
        {pageError && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {pageError}
          </div>
        )}

        {optionsLoading && (
          <div className="mb-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 text-center text-xs font-semibold text-gray-500 shadow-sm">
            Preparing booking options…
          </div>
        )}

        <form onSubmit={submitRequest}>
            {step === 1 && (
              <section className="space-y-4 px-4 pt-4 sm:px-0">
                <div className="rounded-3xl border border-gray-100 bg-white px-4 py-5 shadow-sm sm:p-6">
                  <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-gray-400">
                    Step 1 · Route
                  </p>
                  <h2 className="mt-1 text-xl font-bold tracking-tight text-gray-950">{rapidPilotRideNow ? "Choose your pickup and destination" : "Plan your ride"}</h2>

                  <div className="relative mt-5">
                    <span aria-hidden="true" className="pointer-events-none absolute bottom-8 left-[8px] top-9 w-0.5 bg-gray-200" />

                    <div className="relative flex items-center gap-3 border-b border-gray-100 pb-3">
                      <span aria-hidden="true" className="z-10 h-[17px] w-[17px] shrink-0 rounded-full border-[4px] border-gray-900 bg-white" />
                      <PrivateRideInlineLocationField
                        id="private-ride-inline-pickup"
                        title="Pickup"
                        placeholder="Enter pickup location"
                        point={pickupPoint}
                        displayText={pickupLocation}
                        searchScope={pickupSearchScope}
                        onSelect={(point) => {
                          setPickupPoint(point);
                          setPickupLocation(point.label || point.formattedAddress);
                          void trackPrivateBookingEvent("pickup_location_selected");
                        }}
                        onTextChange={(value) => {
                          setPickupPoint(null);
                          setPickupLocation(value);
                        }}
                        onMapPin={() => openPrivateLocationPicker("pickup")}
                      />
                    </div>

                    {additionalStops.map((stop, index) => (
                      <div key={index} className="relative flex items-center gap-3 border-b border-gray-100 py-3">
                        <span aria-hidden="true" className="z-10 flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[5px] bg-gray-800 text-[10px] font-bold text-white">{index + 1}</span>
                        <PrivateRideInlineLocationField
                          id={"private-ride-inline-stop-" + index}
                          title={"Stop " + (index + 1)}
                          placeholder="Search additional stop"
                          point={stop?.latitude != null && stop?.longitude != null ? stop : null}
                          displayText={stop?.label || ""}
                          autoFocus={stop?.latitude == null}
                          searchScope={destinationSearchScope}
                          onSelect={(point) => setAdditionalStops((items) => items.map((item, i) => i === index ? point : item))}
                          onTextChange={(value) => setAdditionalStops((items) => items.map((item, i) =>
                            i === index ? { label: value, latitude: null, longitude: null } : item
                          ))}
                          onMapPin={() => openPrivateLocationPicker("stop-" + index)}
                        />
                        <div className="flex shrink-0 items-center gap-0.5">
                          {additionalStops.length > 1 && (
                            <button type="button" aria-label={"Move stop " + (index + 1) + (index === 0 ? " down" : " up")}
                              onClick={() => setAdditionalStops((items) => { const next = [...items]; [next[0], next[1]] = [next[1], next[0]]; return next; })}
                              className="rounded-full p-2 text-gray-500 hover:bg-gray-100">
                              {index === 0 ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                            </button>
                          )}
                          <button type="button" aria-label={"Remove stop " + (index + 1)}
                            onClick={() => setAdditionalStops((items) => items.filter((_, i) => i !== index))}
                            className="rounded-full p-2 text-gray-500 hover:bg-red-50 hover:text-red-700">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="relative flex items-center gap-3 pt-3">
                      <span aria-hidden="true" className="z-10 h-[17px] w-[17px] shrink-0 rounded-[4px] bg-[#720e1e]" />
                      <PrivateRideInlineLocationField
                        id="private-ride-inline-destination"
                        title="Destination"
                        placeholder="Where are you going?"
                        point={dropoffPoint}
                        displayText={dropoffLocation}
                        searchScope={destinationSearchScope}
                        onSelect={(point) => {
                          setDropoffPoint(point);
                          setDropoffLocation(point.label || point.formattedAddress);
                          void trackPrivateBookingEvent("dropoff_location_selected");
                        }}
                        onTextChange={(value) => {
                          setDropoffPoint(null);
                          setDropoffLocation(value);
                        }}
                        onMapPin={() => openPrivateLocationPicker("destination")}
                      />
                      <button
                        type="button"
                        aria-label={additionalStops.length >= 2 ? "Maximum of two additional stops" : "Add additional stop"}
                        title={additionalStops.length >= 2 ? "Maximum two stops" : "Add a stop"}
                        disabled={additionalStops.length >= 2}
                        onClick={() => setAdditionalStops((items) => [...items, { label: "", latitude: null, longitude: null }].slice(0, 2))}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#720e1e]/10 text-[#720e1e] transition hover:bg-[#720e1e]/15 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-300"
                      >
                        <Plus className="h-5 w-5" strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                  {additionalStops.length > 0 && (
                    <p className="mt-4 text-xs leading-5 text-gray-500">
                      An included waiting allowance applies at each stop; extra waiting is R100/hour, prorated.
                    </p>
                  )}
                </div>

                <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
                  <div className="flex items-center justify-between px-4 py-3">
                    <p className="text-sm font-bold text-gray-900">Route preview</p>
                    <p className="text-xs font-medium text-gray-500">Stellenbosch and beyond</p>
                  </div>
                  <div className="relative h-44 overflow-hidden bg-[#eef0ed] sm:h-60">
                    <PrivateRideRoutePreview pickup={pickupPoint} stops={additionalStops} destination={dropoffPoint} />
                  </div>
                </div>

                {additionalStops.length > 0 && instantQuote?.quote_id && !instantQuoteLoading && (
                  <p className="px-1 text-xs font-medium text-gray-600">
                    Route estimate: {formatMoney(instantQuote.passenger_total_price)} · {Number(instantQuote.distance_km || 0).toFixed(1)} km
                  </p>
                )}
              </section>
            )}

            {step === 2 && (
              <section className="space-y-4">
                <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                  <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-gray-400">
                    {rapidPilotRideNow ? "Step 2 · Ride details" : "Step 2 · When"}
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-gray-950">{rapidPilotRideNow ? "Confirm your ride details" : "Choose your pickup"}</h2>

                  <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1">
                    <button
                      type="button"
                      onClick={() => handleAsapChange(true)}
                      className={`rounded-xl px-3 py-3 text-sm font-bold transition ${
                        isAsap
                          ? "bg-[#720e1e] text-white shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Ride now
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAsapChange(false)}
                      className={`rounded-xl px-3 py-3 text-sm font-bold transition ${
                        !isAsap
                          ? "bg-white text-gray-950 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Schedule
                    </button>
                  </div>

                  {isAsap ? (
                    <div className="mt-4 rounded-2xl border border-[#720e1e]/15 bg-[#720e1e]/5 p-4">
                      <p className="font-bold text-gray-950">As soon as possible</p>
                      <p className="mt-1 text-sm leading-5 text-gray-600">
                        {isGroupRide
                          ? "UniAir will review vehicle availability and prepare a manual Group Ride quote as quickly as possible."
                           : rapidPilotRideNow
                           ? "Choose UniAir Wallet or a card before we look for a nearby student driver. You’ll see driver availability once payment is secured."
                           : "Your request will be sent to eligible drivers immediately. The ride is confirmed only after a driver accepts and you pay."}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-gray-200 p-4">
                      <div className="grid grid-cols-2 divide-x divide-gray-100">
                        <div className="pr-3">
                          <label
                            htmlFor="private-ride-booking-date"
                            className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400"
                          >
                            Date
                          </label>
                          <input
                            id="private-ride-booking-date"
                            type="date"
                            value={bookingDate}
                            min={minScheduledDate}
                            max={maxBookingDate}
                            onChange={(event) => handlePrivateTravelDateChange(event.target.value)}
                            className="mt-2 w-full bg-transparent text-sm font-semibold text-gray-900 outline-none"
                          />
                        </div>

                        <div className="pl-3">
                          <label
                            htmlFor="private-ride-pickup-time"
                            className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400"
                          >
                            Pickup time
                          </label>
                          <input
                            id="private-ride-pickup-time"
                            type="time"
                            step="900"
                            value={pickupTime}
                            min={
                              bookingDate === minScheduledDate
                                ? minPickupTimeForSelectedDate
                                : undefined
                            }
                            onChange={(event) =>
                              handlePrivatePickupTimeChange(event.target.value)
                            }
                            className="mt-2 w-full bg-transparent text-sm font-semibold text-gray-900 outline-none"
                          />
                        </div>
                      </div>
                      <p className="mt-3 border-t border-gray-100 pt-3 text-xs leading-5 text-gray-500">
                        Book at least {minimumNoticeLabel} ahead. You can schedule up to {settings.max_advance_days || 30} days ahead.
                      </p>
                    </div>
                  )}
                </section>

                <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                      Passengers
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      How many people are travelling?
                    </p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">
                      {isAsap
                        ? "Ride Now accommodates up to 4 passengers in one car. For 5 or more, choose Schedule."
                        : "1–4 passengers use a standard Private Ride. For 5 or more, select Group Ride."}
                    </p>
                  </div>

                  <div className={`mt-4 grid gap-2 ${isAsap ? "grid-cols-4" : "grid-cols-5"}`} role="group" aria-label="Passenger count">
                    {[1, 2, 3, 4].map((count) => {
                      const selected = !isGroupRide && Number(passengerCount) === count;
                      return (
                        <button
                          key={count}
                          type="button"
                          onClick={() => selectStandardPassengerCount(count)}
                          className={`rounded-2xl border px-2 py-3 text-sm font-bold transition ${
                            selected
                              ? "border-[#720e1e] bg-[#720e1e] text-white shadow-sm"
                              : "border-gray-200 bg-white text-gray-800 hover:border-[#720e1e]/35 hover:bg-[#720e1e]/5"
                          }`}
                          aria-pressed={selected}
                        >
                          {count}
                        </button>
                      );
                    })}

                    {!isAsap && (
                      <button
                        type="button"
                        onClick={selectGroupRide}
                        disabled={groupRidesUnavailable}
                        className={`rounded-2xl border px-2 py-3 text-sm font-bold transition ${
                        isGroupRide
                          ? "border-[#720e1e] bg-[#720e1e] text-white shadow-sm"
                          : groupRidesUnavailable
                            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                            : "border-[#d6ad60] bg-[#fdfaf3] text-[#720e1e] hover:bg-[#f7efd9]"
                      }`}
                        aria-pressed={isGroupRide}
                      >
                        5+
                      </button>
                    )}
                  </div>

                  {!isAsap && isGroupRide ? (
                    <div className="mt-4 rounded-2xl border border-[#d6ad60]/50 bg-[#fdfaf3] p-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-950">Group Ride</p>
                          <p className="mt-1 text-xs leading-5 text-gray-600">
                            Groups of 5–8 get an instant two-car quote. If your group needs three or more cars, UniAir will review the vehicle plan and send a manual quote.
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-2 rounded-xl bg-white p-1 shadow-sm">
                          <button
                            type="button"
                            onClick={() =>
                              setPassengerCount((value) =>
                                Math.max(GROUP_RIDE_MIN_PASSENGERS, Number(value || GROUP_RIDE_MIN_PASSENGERS) - 1),
                              )
                            }
                            disabled={Number(passengerCount) <= GROUP_RIDE_MIN_PASSENGERS}
                            className="h-10 w-10 rounded-lg bg-gray-100 text-lg font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Remove group passenger"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min={GROUP_RIDE_MIN_PASSENGERS}
                            max={maxGroupPassengers}
                            inputMode="numeric"
                            value={passengerCount}
                            onChange={(event) => {
                              const next = Number(event.target.value);
                              if (!Number.isFinite(next)) return;
                              setPassengerCount(
                                Math.max(
                                  GROUP_RIDE_MIN_PASSENGERS,
                                  Math.min(maxGroupPassengers, Math.floor(next)),
                                ),
                              );
                            }}
                            className="h-10 w-16 rounded-lg border-0 bg-white px-1 text-center text-base font-bold text-gray-950 outline-none focus:ring-2 focus:ring-[#720e1e]/20"
                            aria-label="Group passenger count"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setPassengerCount((value) =>
                                Math.min(maxGroupPassengers, Number(value || GROUP_RIDE_MIN_PASSENGERS) + 1),
                              )
                            }
                            disabled={Number(passengerCount) >= maxGroupPassengers}
                            className="h-10 w-10 rounded-lg bg-[#720e1e] text-lg font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Add group passenger"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                        <span className="rounded-full bg-[#720e1e]/10 px-2.5 py-1 text-[#720e1e]">
                          Custom quote
                        </span>
                        <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 shadow-sm">
                          No payment until you accept
                        </span>
                        <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 shadow-sm">
                          Up to {maxGroupPassengers} passengers
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs leading-5 text-gray-500">
                      {isAsap
                        ? "Only one car is requested. To travel with 5 or more passengers, use scheduled Private Rides."
                        : "Standard Private Rides are limited to 4 passengers because most UniAir student-driver vehicles have four passenger seats."}
                    </p>
                  )}

                  {!isAsap && groupRidesUnavailable ? (
                    <p className="mt-3 rounded-2xl bg-amber-50 px-3 py-2.5 text-xs leading-5 text-amber-800">
                      {groupRideOptions?.message || "Group Ride requests are not available in your market right now."}
                    </p>
                  ) : null}
                </section>

                <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                        Return journey
                      </p>
                      <p className="mt-1 text-sm text-gray-600">Do you also need a ride back?</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isReturnTrip && isAsap) setReturnMode("separate_return");
                        setIsReturnTrip(!isReturnTrip);
                      }}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition ${isReturnTrip ? "bg-[#720e1e]" : "bg-gray-200"}`}
                      aria-label="Toggle return journey"
                      aria-pressed={isReturnTrip}
                    >
                      <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition ${isReturnTrip ? "left-6" : "left-1"}`} />
                    </button>
                  </div>

                  {isReturnTrip && (
                    <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
                      <div className={isAsap ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 gap-2 sm:grid-cols-2"}>
                        <button
                          type="button"
                          onClick={() => setReturnMode("separate_return")}
                          className={`rounded-2xl border p-3 text-left transition ${
                            returnMode === "separate_return"
                              ? "border-[#720e1e] bg-[#720e1e]/5"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <p className="text-sm font-bold text-gray-950">Return later</p>
                          <p className="mt-1 text-xs leading-5 text-gray-500">{isAsap
                            ? "First confirm your Ride Now. Then review and book a separate scheduled return."
                            : "A different driver may take the return."}</p>
                        </button>
                        {!isAsap && <button
                          type="button"
                          onClick={() => setReturnMode("driver_waits")}
                          className={`rounded-2xl border p-3 text-left transition ${
                            returnMode === "driver_waits"
                              ? "border-[#720e1e] bg-[#720e1e]/5"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <p className="text-sm font-bold text-gray-950">Driver waits</p>
                          <p className="mt-1 text-xs leading-5 text-gray-500">For stops lasting 1–4 hours.</p>
                        </button>}
                      </div>
                      {isAsap && (
                        <p className="rounded-xl bg-gray-50 px-3 py-2.5 text-xs leading-5 text-gray-600">
                          Ride Now covers the outward trip only. Your return has a separate fare,
                          payment and driver assignment. It will not be booked until you confirm it.
                          For a driver to wait and bring you back, choose Schedule instead.
                        </p>
                      )}

                      {returnMode === "separate_return" ? (
                        <div className="rounded-2xl bg-gray-50 p-4">
                          <div className="grid grid-cols-2 divide-x divide-gray-200">
                            <div className="pr-3">
                              <label htmlFor="private-ride-return-date" className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                                Return date
                              </label>
                              <input
                                id="private-ride-return-date"
                                type="date"
                                value={returnDate}
                                min={effectivePickupDate}
                                max={maxBookingDate}
                                onChange={(event) => setReturnDate(event.target.value)}
                                className="mt-2 w-full bg-transparent text-sm font-semibold text-gray-900 outline-none"
                              />
                            </div>
                            <div className="pl-3">
                              <label htmlFor="private-ride-return-time" className="block text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                                Return time
                              </label>
                              <input
                                id="private-ride-return-time"
                                type="time"
                                step="900"
                                value={returnTime}
                                onChange={(event) =>
                                  handlePrivateReturnTimeChange(event.target.value)
                                }
                                className="mt-2 w-full bg-transparent text-sm font-semibold text-gray-900 outline-none"
                              />
                            </div>
                          </div>
                          <p className="mt-3 text-xs leading-5 text-gray-500">
                            Heading home after midnight? Choose the time normally.
                            If it falls before your outbound time, UniAir will move
                            the return to the next day automatically.
                          </p>
                        </div>
                      ) : (
                        <div className="rounded-2xl bg-gray-50 p-4">
                          <p className="text-sm font-semibold text-gray-800">How long should the driver wait?</p>
                          <div className="mt-3 grid grid-cols-4 gap-2">
                            {[1, 2, 3, 4].map((hours) => (
                              <button
                                type="button"
                                key={hours}
                                onClick={() => setWaitingHours(hours)}
                                className={`rounded-xl py-2.5 text-sm font-bold transition ${
                                  Number(waitingHours) === hours
                                    ? "bg-[#720e1e] text-white"
                                    : "bg-white text-gray-700 shadow-sm"
                                }`}
                              >
                                {hours}h
                              </button>
                            ))}
                          </div>
                          {calculatedWaitingReturn && (
                            <p className="mt-3 text-xs leading-5 text-gray-600">
                              Estimated return departure: <b>{formatDate(calculatedWaitingReturn.date)} at {calculatedWaitingReturn.time}</b>.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </section>
            )}

            {step === 3 && (
              <section className="space-y-4">
                <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-gray-400">
                        {rapidPilotRideNow ? "Your Ride Now" : "Your journey"}
                      </p>
                      <h2 className="mt-1 text-xl font-bold text-gray-950">{rapidPilotRideNow ? "Review before finding your driver" : "Review before requesting"}</h2>
                    </div>
                    <button type="button" onClick={() => moveToStep(1)} className="text-sm font-bold text-[#720e1e]">
                      Edit route
                    </button>
                  </div>

                  <div className="mt-4 rounded-2xl bg-[#fdfaf3] p-4">
                    <div className="flex gap-3">
                      <div className="flex flex-col items-center pt-1">
                        <span className="h-3 w-3 rounded-full bg-[#720e1e]" />
                        <span className="my-1 h-9 border-l-2 border-dashed border-[#d6ad60]" />
                        <span className="h-3 w-3 rounded-full border-2 border-[#d6ad60] bg-white" />
                      </div>
                      <div className="min-w-0 flex-1 space-y-4">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">Pickup</p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">{pickupPoint?.label || pickupLocation}</p>
                        </div>
                        {additionalStops.map((stop, index) => (
                          <div key={"review-stop-" + index}>
                            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">Stop {index + 1}</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">{stop.label || stop.formattedAddress}</p>
                          </div>
                        ))}
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">Destination</p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">{dropoffPoint?.label || dropoffLocation}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => moveToStep(2)} className="rounded-2xl bg-gray-50 p-3 text-left">
                      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">Pickup</p>
                      <p className="mt-1 text-sm font-bold text-gray-900">
                        {isAsap ? "As soon as possible" : `${formatDate(bookingDate)} · ${formatTime(pickupTime)}`}
                      </p>
                    </button>
                    <button type="button" onClick={() => moveToStep(2)} className="rounded-2xl bg-gray-50 p-3 text-left">
                      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">Passengers</p>
                      <p className="mt-1 text-sm font-bold text-gray-900">{passengerCount} {passengerCount === 1 ? "person" : "people"}</p>
                    </button>
                  </div>

                  <button type="button" onClick={() => moveToStep(2)} className="mt-3 w-full rounded-2xl bg-gray-50 p-3 text-left">
                    <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">Journey type</p>
                    <p className="mt-1 text-sm font-bold text-gray-900">
                      {!isReturnTrip
                        ? "One-way ride"
                        : rideNowReturnLater
                          ? `Ride Now outward · Return draft ${formatDate(returnDate)} at ${formatTime(returnTime)}`
                        : returnMode === "separate_return"
                          ? `Return later · ${formatDate(returnDate)} at ${formatTime(returnTime)}`
                          : `Driver waits ${waitingHours} hour${Number(waitingHours) === 1 ? "" : "s"}`}
                    </p>
                  </button>
                </section>

                <section className="overflow-hidden rounded-3xl bg-[#171717] p-5 text-white shadow-lg">
                  <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-white/55">
                    {rideNowReturnLater ? "Outward Ride Now fare only" : "Your quote"}
                  </p>

                  {groupQuoteRequired ? (
                    <div className="mt-5">
                      <p className="text-xl font-bold">Group Ride quote required</p>
                      <p className="mt-2 text-sm leading-6 text-white/70">
                        UniAir will review whether a larger vehicle or multiple vehicles make the most financial sense, confirm availability, and send you a final quote before payment opens.
                      </p>
                      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm">
                        <span className="text-white/60">Travellers</span>
                        <span className="float-right font-bold">{passengerCount}</span>
                      </div>
                      <p className="mt-3 text-xs leading-5 text-white/50">
                        Larger-vehicle hire can cost more than a standard 1–4 passenger Private Ride. The trip is only confirmed after you accept the quote and pay.
                      </p>
                    </div>
                  ) : airportTransferRequired ? (
                    <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-[#2a1d09]">
                      <p className="text-lg font-bold">Airport transfer detected</p>
                      <p className="mt-2 text-sm leading-6 text-[#5f4316]">
                        Airport trips must use UniAir Airport Transfers so the correct pickup points and airport pricing apply.
                      </p>
                    </div>
                  ) : instantQuoteLoading ? (
                    <div className="mt-5 text-sm text-white/70">Calculating your fare...</div>
                  ) : manualQuoteRequired ? (
                    <div className="mt-5">
                      <p className="text-xl font-bold">Manual quote required</p>
                      <p className="mt-2 text-sm leading-6 text-white/70">
                        {instantQuoteNotice?.message || "This journey is above the automatic quote limit. Send the details and UniAir will review it."}
                      </p>
                    </div>
                  ) : instantQuoteSummary ? (
                    <div className="mt-4">
                      <p className="text-4xl font-bold">{formatMoney(instantQuoteSummary.total)}</p>
                      <p className="mt-1 text-xs text-white/60">
                        {isInstantTwoCarGroup ? "Group Ride fare · 2 cars" : "Private Ride fare"}
                      </p>

                      {isInstantTwoCarGroup ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="inline-flex rounded-full border border-[#d6ad60]/30 bg-[#d6ad60]/10 px-3 py-1.5 text-xs font-bold text-[#f5dfad]">
                            2 vehicles included
                          </span>
                          {instantQuoteSummary.perPersonPrice > 0 ? (
                            <span className="inline-flex rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold text-white/80">
                              ≈ {formatMoney(instantQuoteSummary.perPersonPrice)} per person
                            </span>
                          ) : null}
                        </div>
                      ) : null}

                      {lateNightPricingEnabled && instantQuoteSummary.lateNightPricingApplied ? (
                        <div className="mt-3 inline-flex rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-xs font-bold text-amber-100">
                          Late-night pricing applies to this pickup time
                        </div>
                      ) : null}

                      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-white/10 pt-4 text-sm">
                        <div>
                          <p className="text-xs text-white/50">Distance</p>
                          <p className="mt-1 font-bold">{instantQuoteSummary.distanceKm.toFixed(1)} km</p>
                        </div>
                        <div>
                          <p className="text-xs text-white/50">Estimated drive</p>
                          <p className="mt-1 font-bold">{instantQuoteSummary.durationMinutes} min</p>
                        </div>
                      </div>

                      <div className="mt-4 border-t border-white/10 pt-4">
                        <p className="text-sm leading-6 text-white/70">
                          {isInstantTwoCarGroup
                            ? isReturnTrip
                              ? "This is one total price for both cars across your complete return Group Ride."
                              : "This is one total price for both cars in your Group Ride."
                            : isReturnTrip
                              ? "This is the single total price for your complete return booking."
                              : "This is the single total price for your Private Ride."}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-white/50">
                          {lateNightPricingEnabled
                            ? "Any applicable operational, waiting or late-night pricing is already included in this one total."
                            : "Any applicable operational or waiting pricing is already included in this one total."}
                        </p>
                      </div>
                    </div>
                  ) : instantQuoteError ? (
                    <div className="mt-5 text-sm leading-6 text-red-200">{instantQuoteError}</div>
                  ) : (
                    <div className="mt-5 text-sm leading-6 text-white/70">
                      Your fare will appear here once the journey details are confirmed.
                    </div>
                  )}
                </section>

                <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                  <label htmlFor="private-ride-notes">
                    <span className="text-xs font-semibold uppercase tracking-[0.10em] text-gray-400">
                      {isGroupRide ? "Notes for UniAir" : "Notes for the driver"}
                    </span>
                    <span className="mt-1 block text-sm text-gray-600">
                      {isGroupRide
                        ? "Optional—add luggage, group, access or trip-planning details."
                        : "Optional—add luggage, gate or landmark instructions."}
                    </span>
                    <textarea
                      id="private-ride-notes"
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder={
                        isGroupRide
                          ? "Anything UniAir should consider when arranging the vehicle and driver?"
                          : "Anything the driver should know?"
                      }
                      className="mt-3 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-[#720e1e] focus:ring-2 focus:ring-[#720e1e]/15"
                    />
                  </label>
                </section>

                <p className="px-2 text-center text-xs leading-5 text-gray-500">
                  {rideNowReturnLater
                    ? "Your Ride Now payment covers the outward trip only. After a driver accepts, you can review and book your return as a separate scheduled ride. Its fare and driver are not yet confirmed."
                    : groupQuoteRequired
                    ? "UniAir will review the vehicle plan and send your quote. Payment confirms the booking; driver details are shared after assignment."
                    : isInstantTwoCarGroup
                      ? "Your two-car request is sent to eligible drivers. Payment opens only after both required vehicle slots are covered."
                      : rapidPilotRideNow
                         ? "Next, choose UniAir Wallet or a card to find your driver. Wallet funds are reserved and returned if no driver is found. Card payments are charged after the completed ride."
                         : "Your request is sent to eligible drivers. Payment is requested only after a driver accepts."}
                </p>
              </section>
            )}

            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
              <div className="mx-auto flex max-w-xl gap-3">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => moveToStep(step - 1)}
                    className="rounded-2xl border border-gray-300 px-5 py-3.5 font-bold text-gray-700 transition hover:bg-gray-50"
                  >
                    Back
                  </button>
                )}

                {step === 1 && (
                  <button
                    type="button"
                    onClick={continueFromWhere}
                    disabled={optionsLoading}
                    className={`flex-1 rounded-2xl py-3.5 font-bold transition ${
                      optionsLoading
                        ? "cursor-not-allowed bg-gray-200 text-gray-400"
                        : "bg-[#720e1e] text-white shadow-lg shadow-[#720e1e]/15 hover:bg-[#5a0b18]"
                    }`}
                  >
                    {optionsLoading ? "Preparing…" : "Continue"}
                  </button>
                )}

                {step === 2 && (
                  <button
                    type="button"
                    onClick={continueFromWhen}
                    className="flex-1 rounded-2xl bg-[#720e1e] py-3.5 font-bold text-white shadow-lg shadow-[#720e1e]/15 transition hover:bg-[#5a0b18]"
                  >
                    Review ride
                  </button>
                )}

                {step === 3 && (
                  <button
                    type={airportTransferRequired ? "button" : "submit"}
                    onClick={airportTransferRequired ? () => navigate("/dashboard") : undefined}
                    disabled={
                      !airportTransferRequired &&
                      (submitting || optionsLoading || instantQuoteLoading || (!manualQuoteRequired && !instantQuoteReady))
                    }
                    className={`flex-1 rounded-2xl py-3.5 font-bold transition ${
                      !airportTransferRequired &&
                      (submitting || optionsLoading || instantQuoteLoading || (!manualQuoteRequired && !instantQuoteReady))
                        ? "cursor-not-allowed bg-gray-200 text-gray-400"
                        : "bg-[#720e1e] text-white shadow-lg shadow-[#720e1e]/15 hover:bg-[#5a0b18]"
                    }`}
                  >
                    {airportTransferRequired
                      ? "Book airport transfer"
                      : submitting
                        ? "Sending request..."
                        : groupQuoteRequired
                          ? "Request Group Ride quote"
                          : isInstantTwoCarGroup
                            ? "Request this Group Ride"
                            : manualQuoteRequired
                              ? "Request manual quote"
                              : rapidPilotRideNow
                                ? "Continue to payment"
                                : "Request this ride"}
                  </button>
                )}
              </div>
            </div>
        </form>
      </main>

      {activeLocationPicker && (
        <UniAirLocationPicker
          key={`private-ride-location-picker-${activeLocationPicker}`}
          brandLabel="Private ride"
          title={activeLocationPicker === "pickup" ? "Set pickup location" : activeLocationPicker.startsWith("stop-") ? "Set additional stop" : "Set destination"}
          searchHint={
            activeLocationPicker === "pickup"
              ? "Search any South African pickup address, residence, gate or landmark"
              : "Search any South African destination"
          }
          value={activeLocationPicker === "pickup" ? pickupPoint : activeLocationPicker.startsWith("stop-") ? additionalStops[Number(activeLocationPicker.split("-")[1])] || null : dropoffPoint}
          areaMode="south_africa"
          searchScope={activeLocationPicker === "pickup" ? pickupSearchScope : destinationSearchScope}
          onClose={() => setActiveLocationPicker(null)}
          onClear={() => {
            if (activeLocationPicker === "pickup") {
              setPickupPoint(null);
              setPickupLocation("");
            } else if (activeLocationPicker.startsWith("stop-")) {
              const index = Number(activeLocationPicker.split("-")[1]);
              setAdditionalStops((items) => items.filter((_, i) => i !== index));
            } else {
              setDropoffPoint(null);
              setDropoffLocation("");
            }
          }}
          onConfirm={(point) => {
            if (activeLocationPicker === "pickup") {
              setPickupPoint(point);
              setPickupLocation(point.label || point.formattedAddress);
              void trackPrivateBookingEvent("pickup_location_selected");

              // Continue straight to destination search for a faster ride-planning flow.
              // Do not replace a destination the passenger has already chosen.
              setActiveLocationPicker(null);
              return;
            }

            if (activeLocationPicker.startsWith("stop-")) {
              const index = Number(activeLocationPicker.split("-")[1]);
              if (index >= 0 && index < 2) {
                setAdditionalStops((items) => { const next = [...items]; next[index] = point; return next.slice(0, 2); });
              }
              setActiveLocationPicker(null);
              return;
            }
            setDropoffPoint(point);
            setDropoffLocation(point.label || point.formattedAddress);
            void trackPrivateBookingEvent("dropoff_location_selected");
            setActiveLocationPicker(null);
          }}
        />
      )}
    </div>
  );

}

function PrivateRideJourneyCard({
  pickupPoint,
  dropoffPoint,
  onEditPickup,
  onEditDestination,
}) {
  const endpoint = ({ label, value, placeholder, action, onClick, tone = "maroon" }) => {
    const isSet = Boolean(value?.label);
    const iconClass =
      tone === "gold"
        ? "border border-[#d6ad60] bg-[#fdfaf3] text-[#9a6b12]"
        : "bg-[#720e1e]/10 text-[#720e1e]";

    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition hover:bg-white/70"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconClass}`}
          aria-hidden="true"
        >
          <PrivateRideLocationIcon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400">
            {label}
          </span>
          <span
            className={`mt-1 block truncate text-sm font-medium ${
              isSet ? "text-gray-900" : "text-gray-400"
            }`}
          >
            {value?.label || placeholder}
          </span>
        </span>
        <span className="shrink-0 text-xs font-semibold text-[#720e1e]">
          {isSet ? "Change" : action}
        </span>
      </button>
    );
  };

  return (
    <section className="rounded-2xl border border-[#ead7c1] bg-[#fdfaf3] p-3 shadow-sm">
      {endpoint({
        label: "Pickup location",
        value: pickupPoint,
        placeholder: "Set your exact pickup location",
        action: "Set location",
        onClick: onEditPickup,
      })}

      <div className="flex items-center gap-3 py-1 pl-[1.13rem]">
        <span className="h-7 border-l-2 border-dashed border-[#d6ad60]" aria-hidden="true" />
      </div>

      {endpoint({
        label: "Destination",
        value: dropoffPoint,
        placeholder: "Set your exact destination",
        action: "Set location",
        onClick: onEditDestination,
        tone: "gold",
      })}
    </section>
  );
}
